import startCase from "lodash.startcase";
import pluralize, { singular } from "pluralize";
import { ReactNode } from "react";

export type CompareWithType = {
  type: "created" | "updated" | "deleted" | "unchanged";
  data: {
    from: CompareDiff;
    to: CompareDiff;
  };
};

export type CompareWithoutType = { [Key: string]: CompareDiff };

export type CompareDiff = CompareWithType | CompareWithoutType;

export interface ChangeLogProps {
  k: string;
  value: any;
  key: string;
  dontFormat?: boolean;
}

export const KEYS_TO_IGNORE = ["builtBy", "listKey"];

const actionOrder = {
  createdKeyValueLine: 0,
  updatedKeyValueLine: 0,
  deleteKeyValueLine: 0,
  createdObject: 1,
  updatedObject: 2,
  deletedObject: 3,
  nestedObject: 4,
  default: 5,
};

export const componentSort = (
  a: { type: string; element: ReactNode },
  b: { type: string; element: ReactNode }
) => {
  return actionOrder[a.type] - actionOrder[b.type];
};

export const findFirstEventType = (obj: object) => {
  if (
    obj &&
    typeof obj === "object" &&
    "type" in obj &&
    ["added", "updated", "deleted"].includes(obj.type as string)
  ) {
    return obj.type;
  }

  for (const [key, value] of Object.entries(obj)) {
    if (value && typeof value === "object") {
      const type = findFirstEventType(value);
      if (type) {
        return type;
      }
    }
  }
};

export const isJsonKey = (key: string): any => {
  const parsed = null;

  try {
    const tryParse = JSON.parse(key);
    // @ts-ignore
    if ("key" in tryParse) {
      return tryParse;
    }
  } catch (e) {
    // nothing
  }

  return parsed;
};

export const formatText = (key: string, text: unknown) => {
  if (text === null) {
    return "null";
  }

  if (text === undefined) {
    return "Not Set";
  }

  if (typeof key === "string" && key.includes("__")) {
    text = key.split("__")[1]!;
  }

  let returnText = `${text as string}`;
  if (typeof text === "string") {
    if (
      key.toLowerCase().includes("id") &&
      !["buildId", "addTestGroupId", "addVariationId"].includes(key)
    ) {
      returnText = text.toLowerCase();
    } else if (["null", "", "undefined"].includes(text.toLowerCase())) {
      returnText = "Not Set";
    } else if (text.includes(".")) {
      returnText = `${text}`;
    } else {
      returnText = startCase(text);
    }
  } else if (text === null || text === undefined) {
    returnText = "Not Set";
  }

  return (
    returnText
      .replace("Id", "ID")
      .replace("id", "ID")
      .replace("wIDgets", "Widgets") || `${text as string}`
  );
};

export const getEntityAndName = (k: string, data: any) => {
  if (data && typeof data === "object" && "listKey" in data) {
    const length = Object.values(data).length - 1;
    return {
      entity: `${length} `,
      name: startCase(pluralize(data["listKey"], length)),
    };
  } else {
    const jsonKey = isJsonKey(k);

    let name = data?.name || data?.title || data?.id || k;
    let entity = "";
    if (jsonKey) {
      entity = `${startCase(singular(jsonKey.key))} `;
      name = jsonKey.name;
    } else {
      name = startCase(name);
    }

    return { entity, name };
  }
};

export const filterByExperience = (
  value: string | undefined,
  configCompare: { diffs?: any }
) => {
  if (!value) {
    return configCompare.diffs;
  }

  if (!configCompare?.diffs) {
    return {};
  }

  const filtered: any = {};
  if (
    configCompare?.diffs &&
    typeof configCompare.diffs === "object" &&
    "experiences" in configCompare.diffs
  ) {
    Object.entries(configCompare.diffs.experiences).forEach(([k, v]) => {
      if (k.toLowerCase().includes(value.toLowerCase())) {
        filtered[k] = v;
      }
    });
  }
  return filtered;
};
